import StaticProxy from '@wiwdata/static-proxy';
import configs from './configs';

const storage = { context: null };

function getContext() {
  if (!storage.context) {
    storage.context = new StaticProxy.AppContext({
      gApiLib: window.gapi,
      gApiKey: configs.GAPI_KEY,
      gApiScopes: configs.GAPI_SCOPES,
      gId: configs.GID,
      gDiscoveryDocs: configs.GAPI_DISCOVERY_DOCS,
      wiwApiEndpoint: configs.getEndpoint(),
    });
  }

  return storage.context;
}

/**
 * Get plans from the backend. Loads all monolith plans by default.
 * @param {Boolean} forCustom - Filter returned plans to those appropriate for Set Custom
 *   Plan based on our business rules.
 */
function getPlans(forCustom = false) {
  return StaticProxy.send(getContext(), 'get_plans', { for_custom_plan: forCustom });
}

/**
 * Submits simple plan change to the backend.
 * @param payload {Object} payload containing account id and new plan id.
 * @return {Promise}
 */
function simplePlanChange(payload) {
  return StaticProxy.send(getContext(), 'simple_plan_change', payload);
}

/**
 * Sets "available plan" via the backend. This affects the display of Small Business vs.
 * Enterprise/Enterprise Lite plans. It can also restrict purchase option down to the
 * specific Custom Plan applied via setCustomPlanOption.
 * @param payload {Object} payload containing account id and plan id.
 * @return {Promise}
 */
function setPricingGroup(payload) {
  return StaticProxy.send(getContext(), 'set_available_pricing_group', payload);
}

/**
 * Get the users in the specified account
 * @param accountId
 *  The account ID for which the users should be pulled.
 */
function getAccountUsers(accountId) {
  return StaticProxy.send(getContext(), 'get_account_users', { account_id: accountId });
}

/**
 * Get the details of a specific When I Work account.
 * @param accountId
 *  The account ID for which account details should be pulled.
 */
function getAccountDetails(accountId) {
  return StaticProxy.send(getContext(), 'get_account', { account_id: accountId });
}

/**
 * Get the transactions of a When I Work account.
 * @param accountId
 *  The account ID for which transactions should be pulled.
 */
function getAccountTransactions(accountId) {
  return StaticProxy.send(getContext(), 'get_transactions', { account_id: accountId });
}

/**
 * Get the the account manual transactions of a When I Work account.
 * @param accountId
 *  The account ID for which transactions should be pulled.
 */
function getManualTransactions(accountId) {
  return StaticProxy.send(
    getContext(), 'get_manual_transactions', { account_id: accountId },
  );
}

/**
 * Post a new transaction
 * @param transaction
 *  An object fully describing the transaction to add.
 */
function addTransaction(transaction) {
  return StaticProxy.send(getContext(), 'add_transaction', transaction);
}

/**
 * Post a new credit
 * @param credit
 *  An object fully describing the credit to add.
 */
function addCredit(credit) {
  return StaticProxy.send(getContext(), 'add_credit', credit);
}

/**
 * Post a new recognition override record.
 * @param override
 *  An object fully describing the credit to add.
 */
function addOverride(override) {
  return StaticProxy.send(getContext(), 'add_override', override);
}

/**
 * Get account revenue overrides.
 * @param accountId
 *  The id of the account of interest.
 */
function getRevenueOverride(accountId) {
  const payload = { account_id: accountId };
  return StaticProxy.send(getContext(), 'get_overrides', payload);
}

/**
 * Extend a trial
 * @param extension
 *  An object fully defining the extension including the account id and
 *  extension length (extend_by).
 */
function extendTrial(extension) {
  return StaticProxy.send(getContext(), 'extend_trial', extension);
}

/**
 * Extend plan expires
 * @param extension
 *  An object fully defining the extension including the account id and
 *  new plan expires (extend_by).
 */
function changePlanExpires(extension) {
  return StaticProxy.send(getContext(), 'change_plan_expires', extension);
}

/**
 * Extend attendance expires
 * @param extension
 *  An object fully defining the extension including the account id and
 *  new attendance expires (change_to).
 */
function changeAttendanceExpires(extension) {
  return StaticProxy.send(getContext(), 'change_attendance_expires', extension);
}

/**
 * Clear billing info for an account
 * @param payload
 *  An object containing the account id that should have the billing info
 *  cleared
 */
function clearBillingInfo(payload) {
  return StaticProxy.send(getContext(), 'clear_billing_info', payload);
}

/**
 * Set the tax exempt status for an account
 * @param payload
 *  An object containing the account id as well as a boolean value for tax
 *  exempt status.
 */
function setTaxExemptStatus(payload) {
  return StaticProxy.send(getContext(), 'set_exemption_status', payload);
}

/**
 * Refund a portion or all of a transaction on an account
 * @param payload
 *  An object containing the transaction id as well as the amount to refund.
 */
function submitRefund(payload) {
  return StaticProxy.send(getContext(), 'submit_refund', payload);
}

/**
 * Transfer the account holder status from fromUser to toUser.
 * @param fromUser
 *  The id of the user the account is being transfered from
 * @param toUser
 *  The id of the user the account is being transfered to
 */
function transferAccount(fromUser, toUser) {
  return StaticProxy.send(
    getContext(),
    'transfer_account',
    { from_user_id: fromUser, to_user_id: toUser },
  );
}

/**
 * Set the day of the month that an account is billed
 * @param payload
 *  An object containing the account id and the day to switch to.
 */
function setBillingDay(payload) {
  return StaticProxy.send(getContext(), 'set_billing_day', payload);
}

/**
 * Set the discount to be applied to future transactions on an account
 * @param payload
 *  An object containing the account id and the discount as 0 < discount < 1
 */
function setBillingDiscount(payload) {
  return StaticProxy.send(getContext(), 'set_billing_discount', payload);
}

/**
 * Set the billing status of an account including whether they are enable
 * and whether to reset their retry count.
 * @param payload
 *  An object containing account_id, is_enabled, do_reset
 */
function setBillingStatus(payload) {
  return StaticProxy.send(getContext(), 'set_billing_status', payload);
}

/**
 * Set the custom plan that an account is able to select into
 * @param payload
 *  An object containing the account id and the plan id of the custom plan.
 */
function setCustomPlanOption(payload) {
  return StaticProxy.send(getContext(), 'set_custom_plan', payload);
}

/**
 * Set revenue dates for the given account. Currently only accepts converted_at
 * and created_at. Both are required.
 * @param payload
 *  An object containing the account id and the converted_at as ISO8601 timestamp.
 */
function setRevenueDates(payload) {
  return StaticProxy.send(getContext(), 'set_revenue_dates', payload);
}

/**
 * Submits skip billing action to the backend. This function will skip the next billing date for
 * customer in trouble due to the COVID-19.
 * @param payload {Object} Object with account_id and the internal_notes.
 * @return {Promise<Object>}
 */
function skipBilling(payload) {
  return StaticProxy.send(getContext(), 'covid19_billing_skip', payload);
}

/**
 * Submits resume billing action to the backend. This action will send an email to account holder
 * and managers that their billing will resume. This is intended to be used for covid-19
 * impacted accounts
 * @param payload { Object } The payload containing account_id and email_type
 * @return {Promise}
 */
function resumeBilling(payload) {
  return StaticProxy.send(getContext(), 'resume_billing', payload);
}

/**
 * Deletes the account
 * @param payload {Object} Payload containing the account_id
 * @return {Promise}
 */
function deleteAccount(payload) {
  return StaticProxy.send(getContext(), 'delete_account', payload);
}

/**
 * Undeletes the account
 * @param payload {Object} Payload containing the account_id
 * @return {Promise}
 */
function undeleteAccount(payload) {
  return StaticProxy.send(getContext(), 'undelete_account', payload);
}

/**
 * Get a list of accounts for the given search term.
 * @param query
 *  A string for which we should search for matching accounts.
 */
function searchAccounts(query, start, size, filterQuery) {
  return StaticProxy.send(
    getContext(),
    'search_accounts',
    {
      query,
      start: Number(start),
      size: Number(size),
      filter_query: filterQuery,
    },
  );
}

/**
 * Get a list of users for the given search term.
 * @param query
 *  A string for which we should search for matching users.
 */
function searchUsers(query, start, size, filterQuery) {
  return StaticProxy.send(
    getContext(),
    'search_users',
    {
      query,
      start: Number(start),
      size: Number(size),
      filter_query: filterQuery,
    },
  );
}

/**
 * Return api keys for the account
 * @param payload {Object} Payload containing the account_id
 */
function getApiKeys(payload) {
  return StaticProxy.send(getContext(), 'get_applications', payload);
}

/**
 * Update application record for the application id.
 * @param payload {Object} Payload containing the application_id, account_id, update_values.
 * update_values will be a object with the column and value to update.
 * @return {Promise} A promise that contains the API Keys for the account.
 */
function updateApplication(payload) {
  return StaticProxy.send(getContext(), 'update_application', payload);
}

/**
 * Update account.text_credits
 * @param payload {Object} Payload containing the account_id, and the new text_credits.
 * @return {Promise} A promise with request code.
 */
function updateAccountTextCredits(payload) {
  return StaticProxy.send(getContext(), 'update_text_credits', payload);
}

/**
 * Reactivates the account. That will reset is_deactivated to False and deactivated_at to null.
 * @param payload {Object} Payload containing the account_id.
 * @return {Promise} A promise with request code.
 */
function reactivateAccount(payload) {
  return StaticProxy.send(getContext(), 'reactivate_account', payload);
}

/**
 * Regenerate application.key and updates monolith with the new key..
 * @param payload {Object} Payload containing the application_id, and account_id.
 * update_values will be a object with the column and value to update.
 * @return {Promise} A promise that contains the API Keys for the account.
 */
function regenerateApplicationKey(payload) {
  return StaticProxy.send(getContext(), 'regenerate_application_key', payload);
}

/**
 * Update free account to the enterprise trial plan.
 * @param payload {Object} Payload containing the account_id, and plan_expires.
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function freeToEnterpriseTrial(payload) {
  return StaticProxy.send(getContext(), 'free_to_enterprise_trial', payload);
}

/**
 * Insert application key to applications table in monolith.
 * @param payload {Object} Payload containing name, email, is_disabled, access, and account_id.
 * update_values will be a object with the column and value to update.
 * @return {Promise} A promise that contains the API Keys for the account.
 */
function insertApplicationKey(payload) {
  return StaticProxy.send(getContext(), 'insert_application', payload);
}

/**
 * Set parent id for the account.
 * @param payload {Object} Payload containing account_id and new parent_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function setParent(payload) {
  return StaticProxy.send(getContext(), 'set_parent', payload);
}

/**
 * Get all children for the parent account.
 * @param payload {Object} Payload containing parent_id
 * @return {Promise} A promise that contains children array.
 */
function getChildren(payload) {
  return StaticProxy.send(getContext(), 'get_children', payload);
}

/**
 * Removes all children from the account. Also makes it a stand alone account.
 * @param payload {Object} Payload containing parent_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function removeAllChildren(payload) {
  return StaticProxy.send(getContext(), 'remove_all_children', payload);
}

/**
 * Get role action mappings from the backend.
 * @return {Promise} A promise that contains role action mappings.
 */
function getRoleActionMappings() {
  return StaticProxy.send(getContext(), 'get_role_action_mappings', {});
}

/**
 * Get my roles from the backend.
 * @return {Promise} A promise that contains role action mappings.
 */
function getMyRoles() {
  return StaticProxy.send(getContext(), 'get_my_roles', {});
}

/**
 * Update the specified subscription. Must be a 'free-trial' type and have a valid
 * plan_id.
 *
 * @param payload {Object} - {account_id: int, subscription_id: int,
 *                            plan_id: int, expires_at: ISO8601)
 */
function updateTrialSubscription(payload) {
  return StaticProxy.send(getContext(), 'update_trial_subscription', payload);
}

/**
 * Start a free-trial type Subscription for the given Account, on the given Plan
 * plan_id.
 *
 * @param payload {Object} - {account_id: int, plan_id: int, expires_at: ISO8601)
 */
function startTrialSubscription(payload) {
  return StaticProxy.send(getContext(), 'start_trial_subscription', payload);
}

/**
 * Reset an account to the old billing engine. Only valid for accounts running
 * billing engine 5.
 *
 * @param payload {Object} Payload containing the account_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function resetBillingEngine(payload) {
  return StaticProxy.send(getContext(), 'reset_billing_engine', payload);
}

/**
 * Migrate an account to the new billing engine. Only valid for accounts running
 * billing engine 1.
 *
 * @param payload {Object} Payload containing the account_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function migrateBillingEngine(payload) {
  return StaticProxy.send(getContext(), 'migrate_billing_engine', payload);
}

/**
 * Assign a parent account to a shell, stripping the account of its children.
 * The shell becomes the new parent of the parent and its former child accounts.
 *
 * @param payload {Object} Payload containing the account_id and shell_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function assignShellAccount(payload) {
  return StaticProxy.send(getContext(), 'assign_shell_account', payload);
}

/**
 * Create a backdated subscription for the account through the billing service.
 *
 * @param payload {Object} Payload containing the account_id, plan_id, start_date,
 * and seats
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function createBackdatedSubscription(payload) {
  return StaticProxy.send(getContext(), 'create_backdated_subscription', payload);
}

/**
 * Reset the tax rate of an account. Only valid for accounts running
 * billing engine 5.
 *
 * @param payload {Object} Payload containing the account_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function updateTaxRate(payload) {
  return StaticProxy.send(getContext(), 'update_tax_rate', payload);
}

/**
 * Sync the account's users to Check.
 *
 * @param payload {Object} Payload containing the account_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function payrollSyncUsers(payload) {
  return StaticProxy.send(getContext(), 'payroll_sync_users', payload);
}

/**
 * Sync the account's workplaces to Check.
 *
 * @param payload {Object} Payload containing the account_id
 * @return {Promise} A promise that contains the success or failure status_code.
 */
function payrollSyncWorkplaces(payload) {
  return StaticProxy.send(getContext(), 'payroll_sync_workplaces', payload);
}

function login() {
  return StaticProxy.signIn(getContext());
}

function logout() {
  return StaticProxy.signOut(getContext());
}

function initialize() {
  return StaticProxy.initialize(getContext());
}

export default {
  addCredit,
  addOverride,
  addTransaction,
  assignShellAccount,
  changeAttendanceExpires,
  changePlanExpires,
  clearBillingInfo,
  createBackdatedSubscription,
  deleteAccount,
  extendTrial,
  freeToEnterpriseTrial,
  getAccountDetails,
  getAccountTransactions,
  getAccountUsers,
  getApiKeys,
  getChildren,
  getManualTransactions,
  getMyRoles,
  getPlans,
  getRevenueOverride,
  getRoleActionMappings,
  initialize,
  insertApplicationKey,
  login,
  logout,
  migrateBillingEngine,
  payrollSyncUsers,
  payrollSyncWorkplaces,
  reactivateAccount,
  regenerateApplicationKey,
  removeAllChildren,
  resetBillingEngine,
  resumeBilling,
  searchAccounts,
  searchUsers,
  setPricingGroup,
  setBillingDay,
  setBillingDiscount,
  setBillingStatus,
  setCustomPlanOption,
  setParent,
  setRevenueDates,
  setTaxExemptStatus,
  simplePlanChange,
  startTrialSubscription,
  skipBilling,
  submitRefund,
  transferAccount,
  undeleteAccount,
  updateAccountTextCredits,
  updateApplication,
  updateTaxRate,
  updateTrialSubscription,
};
